import { getValidAccessToken } from '../../../lib/supabase';
import type {
  AdminCommsStatus,
  AdminDashboardStats,
  AdminDeleteUsersResult,
  AdminEmailTemplateRow,
  AdminEmailTemplateUpdate,
  AdminFinanceStats,
  AdminIntegrationRow,
  AdminIntegrationStats,
  AdminInvoiceRow,
  AdminListResponse,
  AdminOrganizationDetail,
  AdminOrganizationRow,
  AdminUserRow,
  AdminWhatsappCommsSettings,
  AdminWhatsappInstanceRow,
  AdminWhatsappSendLogRow,
  AdminWhatsappStats,
  AdminWhatsappSystemInstance,
  AdminWhatsappTemplateRow,
} from '../../../domain/superadmin/types';
import { SuperadminApiError } from './errors';

type ListParams = {
  id?: string;
  page?: number;
  pageSize?: number;
  q?: string;
  plan?: string;
  status?: string;
  subscriptionStatus?: string;
  statusBucket?: string;
  provider?: string;
  type?: string;
  expiringSoon?: string;
  roleFilter?: string;
  missingPhone?: string;
  from?: string;
  to?: string;
  sort?: string;
  limit?: string;
  offset?: string;
};

async function adminFetch<T>(path: string, params?: ListParams): Promise<T> {
  const accessToken = await getValidAccessToken();
  const url = new URL(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${path}`);
  if (params?.id) url.searchParams.set('id', params.id);
  if (params?.page) url.searchParams.set('page', String(params.page));
  if (params?.pageSize) url.searchParams.set('pageSize', String(params.pageSize));
  if (params?.q) url.searchParams.set('q', params.q);
  if (params?.plan) url.searchParams.set('plan', params.plan);
  if (params?.status) url.searchParams.set('status', params.status);
  if (params?.subscriptionStatus) {
    url.searchParams.set('subscriptionStatus', params.subscriptionStatus);
  }
  if (params?.statusBucket) url.searchParams.set('statusBucket', params.statusBucket);
  if (params?.provider) url.searchParams.set('provider', params.provider);
  if (params?.type) url.searchParams.set('type', params.type);
  if (params?.expiringSoon) url.searchParams.set('expiringSoon', params.expiringSoon);
  if (params?.roleFilter && params.roleFilter !== 'all') {
    url.searchParams.set('roleFilter', params.roleFilter);
  }
  if (params?.missingPhone) url.searchParams.set('missingPhone', params.missingPhone);
  if (params?.from) url.searchParams.set('from', params.from);
  if (params?.to) url.searchParams.set('to', params.to);
  if (params?.sort) url.searchParams.set('sort', params.sort);
  if (params?.limit) url.searchParams.set('limit', params.limit);
  if (params?.offset) url.searchParams.set('offset', params.offset);

  let response: Response;
  try {
    response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
      },
    });
  } catch {
    throw new SuperadminApiError(
      'Não foi possível conectar à API admin. Verifique a rede ou se as Edge Functions estão deployadas.',
      0,
      'NETWORK'
    );
  }

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new SuperadminApiError(
      err.error || err.message || 'Falha na API admin',
      response.status,
      err.code
    );
  }

  return response.json();
}

export function fetchAdminDashboardStats(params?: {
  from?: string;
  to?: string;
}): Promise<AdminDashboardStats> {
  const q: ListParams = {};
  if (params?.from) q.from = params.from;
  if (params?.to) q.to = params.to;
  return adminFetch('admin-dashboard-stats', q);
}

export function fetchAdminOrganizations(
  params: ListParams = {}
): Promise<AdminListResponse<AdminOrganizationRow>> {
  return adminFetch('admin-list-organizations', params);
}

export function fetchAdminOrganizationDetail(
  organizationId: string
): Promise<AdminOrganizationDetail> {
  return adminFetch('admin-organization-detail', { id: organizationId });
}

export function fetchAdminUsers(
  params: ListParams = {}
): Promise<AdminListResponse<AdminUserRow>> {
  return adminFetch('admin-list-users', params);
}

export function fetchAdminFinanceStats(): Promise<AdminFinanceStats> {
  return adminFetch('admin-finance-stats');
}

export function fetchAdminInvoices(
  params: ListParams = {}
): Promise<AdminListResponse<AdminInvoiceRow>> {
  return adminFetch('admin-list-invoices', params);
}

export function fetchAdminWhatsappStats(): Promise<AdminWhatsappStats> {
  return adminFetch('admin-whatsapp-stats');
}

export function fetchAdminWhatsappInstances(
  params: ListParams = {}
): Promise<AdminListResponse<AdminWhatsappInstanceRow>> {
  return adminFetch('admin-list-whatsapp-instances', params);
}

export function fetchAdminIntegrationStats(): Promise<AdminIntegrationStats> {
  return adminFetch('admin-integration-stats');
}

export function fetchAdminIntegrations(
  params: ListParams = {}
): Promise<AdminListResponse<AdminIntegrationRow>> {
  return adminFetch('admin-list-integrations', params);
}

export function fetchAdminCommsStatus(): Promise<AdminCommsStatus> {
  return adminFetch('admin-comms-status');
}

export function fetchAdminEmailTemplates(): Promise<{ items: AdminEmailTemplateRow[] }> {
  return adminFetch('admin-list-email-templates');
}

export function fetchAdminWhatsappTemplates(): Promise<{ items: AdminWhatsappTemplateRow[] }> {
  return adminFetch('admin-list-whatsapp-templates');
}

export function fetchAdminWhatsappSystemInstance(): Promise<AdminWhatsappSystemInstance> {
  return adminFetch('admin-whatsapp-system-instance');
}

export function connectAdminWhatsappSystemInstance(): Promise<AdminWhatsappSystemInstance> {
  return adminPost('admin-whatsapp-system-instance', { action: 'connect' });
}

export function reconnectAdminWhatsappSystemInstance(): Promise<AdminWhatsappSystemInstance> {
  return adminPost('admin-whatsapp-system-instance', { action: 'reconnect' });
}

export function disconnectAdminWhatsappSystemInstance(): Promise<AdminWhatsappSystemInstance> {
  return adminPost('admin-whatsapp-system-instance', { action: 'disconnect' });
}

export function fetchAdminWhatsappCommsSettings(): Promise<AdminWhatsappCommsSettings> {
  return adminFetch('admin-whatsapp-comms-settings');
}

export async function updateAdminWhatsappCommsSettings(
  commsEnabled: boolean,
): Promise<AdminWhatsappCommsSettings> {
  const accessToken = await getValidAccessToken();
  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-whatsapp-comms-settings`;
  let response: Response;
  try {
    response = await fetch(url, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ commsEnabled }),
    });
  } catch {
    throw new SuperadminApiError(
      'Não foi possível conectar à API admin. Verifique a rede ou se as Edge Functions estão deployadas.',
      0,
      'NETWORK'
    );
  }
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new SuperadminApiError(
      err.error || err.message || 'Falha na API admin',
      response.status,
      err.code
    );
  }
  return response.json();
}

export async function updateAdminWhatsappTemplateDelivery(
  key: string,
  deliveryActive: boolean,
): Promise<AdminWhatsappTemplateRow> {
  const accessToken = await getValidAccessToken();
  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-update-whatsapp-template`;
  let response: Response;
  try {
    response = await fetch(url, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ key, deliveryActive }),
    });
  } catch {
    throw new SuperadminApiError(
      'Não foi possível conectar à API admin. Verifique a rede ou se as Edge Functions estão deployadas.',
      0,
      'NETWORK'
    );
  }
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new SuperadminApiError(
      err.error || err.message || 'Falha na API admin',
      response.status,
      err.code
    );
  }
  return response.json();
}

export function fetchAdminWhatsappSendLog(params?: {
  limit?: number;
  offset?: number;
}): Promise<{
  items: AdminWhatsappSendLogRow[];
  total: number;
  limit: number;
  offset: number;
}> {
  return adminFetch('admin-list-whatsapp-send-log', {
    limit: String(params?.limit ?? 50),
    offset: String(params?.offset ?? 0),
  });
}

export async function updateAdminEmailTemplate(
  key: string,
  payload: AdminEmailTemplateUpdate
): Promise<AdminEmailTemplateRow> {
  const accessToken = await getValidAccessToken();
  const url = new URL(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-update-email-template`
  );
  url.searchParams.set('key', key);
  let response: Response;
  try {
    response = await fetch(url.toString(), {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
  } catch {
    throw new SuperadminApiError(
      'Não foi possível conectar à API admin. Verifique a rede ou se as Edge Functions estão deployadas.',
      0,
      'NETWORK'
    );
  }
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new SuperadminApiError(
      err.error || err.message || 'Falha na API admin',
      response.status,
      err.code
    );
  }
  return response.json();
}

export async function sendAdminTestEmail(payload: {
  key: string;
  to: string;
  from: string;
}): Promise<{ ok: true }> {
  const accessToken = await getValidAccessToken();
  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-send-test-email`;
  let response: Response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
  } catch {
    throw new SuperadminApiError(
      'Não foi possível conectar à API admin. Verifique a rede ou se as Edge Functions estão deployadas.',
      0,
      'NETWORK'
    );
  }
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new SuperadminApiError(
      err.error || err.message || 'Falha na API admin',
      response.status,
      err.code
    );
  }
  return response.json();
}

export async function sendAdminTestWhatsapp(payload: {
  key: string;
  to: string;
}): Promise<{ ok: true }> {
  const accessToken = await getValidAccessToken();
  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-send-test-whatsapp`;
  let response: Response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
  } catch {
    throw new SuperadminApiError(
      'Não foi possível conectar à API admin. Verifique a rede ou se as Edge Functions estão deployadas.',
      0,
      'NETWORK'
    );
  }
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new SuperadminApiError(
      err.error || err.message || 'Falha na API admin',
      response.status,
      err.code,
      err.detail
    );
  }
  return response.json();
}

export async function deleteAdminUsers(
  userIds: string[],
  options?: { organizationId?: string },
): Promise<AdminDeleteUsersResult> {
  const accessToken = await getValidAccessToken();
  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-delete-users`;
  const body: { userIds: string[]; organizationId?: string } = { userIds };
  if (options?.organizationId) {
    body.organizationId = options.organizationId;
  }
  let response: Response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
  } catch {
    throw new SuperadminApiError(
      'Não foi possível conectar à API admin. Verifique a rede ou se as Edge Functions estão deployadas.',
      0,
      'NETWORK'
    );
  }
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new SuperadminApiError(
      err.error || 'Falha ao excluir usuários',
      response.status,
      err.code
    );
  }
  return response.json();
}

async function adminPost<T>(path: string, body: Record<string, unknown>): Promise<T> {
  const accessToken = await getValidAccessToken();
  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${path}`;
  let response: Response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
  } catch {
    throw new SuperadminApiError(
      'Não foi possível conectar à API admin. Verifique a rede ou se as Edge Functions estão deployadas.',
      0,
      'NETWORK'
    );
  }
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new SuperadminApiError(
      err.error || err.message || 'Falha na API admin',
      response.status,
      err.code
    );
  }
  return response.json();
}

export function blockAdminOrganization(
  organizationId: string,
  reason: string
): Promise<{ ok: true }> {
  return adminPost('admin-org-block', { organizationId, action: 'block', reason });
}

export function unblockAdminOrganization(
  organizationId: string,
  reason: string
): Promise<{ ok: true }> {
  return adminPost('admin-org-block', { organizationId, action: 'unblock', reason });
}

export function saveAdminOrganizationNote(
  organizationId: string,
  body: string
): Promise<{ ok: true }> {
  return adminPost('admin-org-note', { organizationId, body });
}

export type AdminImpersonationStartResult = {
  sessionId: string;
  organizationId: string;
  expiresAt: string;
  membershipCreated: boolean;
};

export function startAdminImpersonation(
  organizationId: string,
  reason: string
): Promise<AdminImpersonationStartResult> {
  return adminPost('admin-impersonation-start', { organizationId, reason });
}

export function endAdminImpersonation(
  sessionId?: string
): Promise<{ ok: true; ended: boolean }> {
  return adminPost('admin-impersonation-end', sessionId ? { sessionId } : {});
}

export {
  fetchAdminTickets,
  fetchAdminTicket,
  updateAdminTicket,
  replyAdminTicket,
} from './adminTicketsApi';
